/* The notification-bell button for the TopNavigation utility (descriptor)  */
import { TopNavigationProps } from '@amzn/awsui-components-core';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Status } from 'src/models/AuthContextModels';
import { AppDispatch, RootState } from 'src/store/store';
import { isUnread } from './notificationModels';
import { markNotificationsReadThunk } from './notificationsSlice';

export const useNotificationBell = (onOpen: () => void): TopNavigationProps.Utility => {
  const dispatch = useDispatch<AppDispatch>();
  const items = useSelector((state: RootState) => state.notificationsStore.items);
  const status = useSelector((state: RootState) => state.notificationsStore.status);
  const unreadCount = useMemo(() => items.filter(isUnread).length, [items]);
  // Persistent items count as unread in the badge (pending until resolved) but the read watermark
  // can't clear them — so only dispatch mark-read when there's a NON-persistent unread item to clear.
  // Otherwise every bell click would fire markNotificationsRead pointlessly while a persistent item exists.
  const hasMarkableUnread = useMemo(() => items.some((n) => !n.is_read && !n.is_persistent), [items]);

  return useMemo<TopNavigationProps.Utility>(
    () => ({
      type: 'button',
      iconName: 'notification',
      ariaLabel: 'Notifications',
      badge: unreadCount > 0,
      text: unreadCount > 0 ? String(unreadCount) : undefined,
      title: 'Notifications',
      onClick: () => {
        // Only mark-read once a baseline fetch has settled (else an in-flight fetch's fulfilled
        // handler resolves with pre-mark-read data and reverts the optimistic grey), and only when
        // there is a markable (non-persistent) unread item.
        if (hasMarkableUnread && status === Status.Completed) {
          dispatch(markNotificationsReadThunk());
        }
        onOpen();
      }
    }),
    [hasMarkableUnread, unreadCount, status, dispatch, onOpen]
  );
};
